import { AudioDebugInfo, CharacterMode, CombustionProfileId, DriveMode, EngineProfileId, EVMusicalState, EVSoundDebugInfo, EVSoundState } from '../types';
import { ENGINE_PROFILES, isEVProfile } from '../config/engines';
import { EV_SOUND_PROFILES } from '../config/evProfiles';
import { EngineSynth } from './EngineSynth';
import { EVSoundEngine } from './EVSoundEngine';
import { AudioEffects } from './AudioEffects';
import { SampleEngineLayer } from './SampleEngineLayer';

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private masterGain: GainNode | null = null;

  private synth: EngineSynth | null = null;
  private evSynth: EVSoundEngine | null = null;
  private effects: AudioEffects | null = null;
  private sampleLayer: SampleEngineLayer | null = null;

  private currentProfileId: EngineProfileId = 'v8';
  private currentCharacter: CharacterMode = 'sport';
  private currentDriveMode: DriveMode = 'COMFORT';
  private masterVolume: number = 0.85;
  private isMuted: boolean = false;
  private isRunning: boolean = false;

  private currentRpm: number = 750;
  private currentEffort: number = 0;
  private currentEngineBraking: number = 0;

  /**
   * Initialize AudioContext upon user gesture
   */
  public async initialize(): Promise<boolean> {
    if (this.ctx && this.ctx.state !== 'closed') {
      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }
      return true;
    }

    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

      if (!AudioContextClass) {
        console.error('Web Audio API is not supported in this browser.');
        return false;
      }

      this.ctx = new AudioContextClass();

      if (this.ctx.state === 'suspended') {
        await this.ctx.resume();
      }

      // Master dynamics compressor to glue audio and prevent clipping
      this.compressor = this.ctx.createDynamicsCompressor();
      this.compressor.threshold.setValueAtTime(-18, this.ctx.currentTime);
      this.compressor.knee.setValueAtTime(12, this.ctx.currentTime);
      this.compressor.ratio.setValueAtTime(6, this.ctx.currentTime);
      this.compressor.attack.setValueAtTime(0.003, this.ctx.currentTime);
      this.compressor.release.setValueAtTime(0.15, this.ctx.currentTime);

      // Master output volume gain
      this.masterGain = this.ctx.createGain();
      const initialGain = this.isMuted ? 0.0 : this.masterVolume;
      this.masterGain.gain.setValueAtTime(initialGain, this.ctx.currentTime);

      // Chain: Compressor -> MasterGain -> Destination
      this.compressor.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);

      // 1. Initialize Combustion Synthesizer
      const combustionId = isEVProfile(this.currentProfileId) ? 'v8' : (this.currentProfileId as CombustionProfileId);
      const profile = ENGINE_PROFILES[combustionId] || ENGINE_PROFILES.v8;
      this.synth = new EngineSynth(this.ctx, this.compressor, profile, this.currentCharacter);

      // 2. Initialize EV Soundscape Engine
      const evId = isEVProfile(this.currentProfileId) ? this.currentProfileId : 'ev_futuristic';
      this.evSynth = new EVSoundEngine(this.ctx, this.compressor, evId, this.currentDriveMode);

      // 3. Initialize Sound Effects (combustion shift pops, clicks)
      this.effects = new AudioEffects(this.ctx, this.compressor);

      // 4. Initialize Sample Layer Architecture
      this.sampleLayer = new SampleEngineLayer(this.ctx, this.compressor);

      return true;
    } catch (e) {
      console.error('Error initializing AudioEngine:', e);
      return false;
    }
  }

  /**
   * Start audio playback
   */
  public async start(): Promise<void> {
    if (!this.ctx) {
      const ok = await this.initialize();
      if (!ok) return;
    }

    if (this.ctx && this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }

    this.isRunning = true;

    if (isEVProfile(this.currentProfileId)) {
      if (this.evSynth) {
        this.evSynth.start();
      }
    } else {
      if (this.synth) {
        this.synth.start();
      }
    }
  }

  /**
   * Stop all audio playback
   */
  public stop(): void {
    if (this.synth) {
      this.synth.stop();
    }
    if (this.evSynth) {
      this.evSynth.stop();
    }
    if (this.sampleLayer) {
      this.sampleLayer.stop();
    }
    this.isRunning = false;
  }

  public isAudioRunning(): boolean {
    return this.isRunning && this.ctx !== null && this.ctx.state === 'running';
  }

  /**
   * Update virtual RPM (Combustion engine only)
   */
  public setRPM(rpm: number): void {
    if (isEVProfile(this.currentProfileId)) return;
    this.currentRpm = rpm;
    if (this.synth) {
      this.synth.updateParameters(this.currentRpm, this.currentEffort, this.currentEngineBraking);
    }
    if (this.sampleLayer) {
      this.sampleLayer.update(this.currentRpm, this.currentEffort);
    }
  }

  /**
   * Update acceleration effort (0.0 to 1.0)
   */
  public setThrottle(effort: number): void {
    this.currentEffort = Math.max(0, Math.min(1, effort));
    if (!isEVProfile(this.currentProfileId) && this.synth) {
      this.synth.updateParameters(this.currentRpm, this.currentEffort, this.currentEngineBraking);
    }
  }

  /**
   * Update engine braking intensity (0.0 to 1.0)
   */
  public setEngineBraking(intensity: number): void {
    this.currentEngineBraking = Math.max(0, Math.min(1, intensity));
    if (!isEVProfile(this.currentProfileId) && this.synth) {
      this.synth.updateParameters(this.currentRpm, this.currentEffort, this.currentEngineBraking);
    }
  }

  /**
   * Direct EV Parameter Update (Speed, Acceleration, Throttle, DriveMode)
   * Zero RPM • Pure Continuous Propulsion Field
   */
  public updateEVParameters(
    speedKmh: number,
    acceleration: number,
    throttle: number,
    brake: number = 0,
    mode: DriveMode = this.currentDriveMode
  ): EVSoundState | null {
    if (this.evSynth && isEVProfile(this.currentProfileId)) {
      if (this.isRunning && !this.evSynth.getIsRunning()) {
        this.evSynth.start();
      }
      return this.evSynth.update(speedKmh, acceleration, throttle, brake, mode);
    }
    return null;
  }

  /**
   * Trigger gear shift sound event (combustion engines only)
   */
  public onShift(direction: 'up' | 'down', mode: DriveMode = 'COMFORT'): void {
    // EV soundscapes have NO gears and NO shift sound effects
    if (isEVProfile(this.currentProfileId)) return;
    if (!this.effects || !this.ctx || !this.isRunning) return;

    if (direction === 'up') {
      const popProbability = mode === 'SPORT' ? 0.85 : mode === 'COMFORT' ? 0.35 : 0.05;
      if (Math.random() < popProbability && this.currentEffort > 0.2) {
        this.effects.triggerShiftPop(Math.max(0.4, this.currentEffort));
      } else {
        this.effects.triggerShiftEngagement();
      }
    } else {
      this.effects.triggerShiftEngagement();
    }
  }

  /**
   * Set DriveMode acoustic character
   */
  public setDriveMode(mode: DriveMode): void {
    this.currentDriveMode = mode;

    if (this.evSynth) {
      this.evSynth.setMode(mode);
    }

    const characterMap: Record<string, CharacterMode> = {
      ECO: 'smooth',
      COMFORT: 'sport',
      SPORT: 'aggressive',
    };
    const mappedCharacter = characterMap[mode] || 'sport';
    this.setCharacterMode(mappedCharacter);
  }

  /**
   * Switch sound profile (Combustion or EV Soundscape) with seamless zero-click crossfade
   */
  public setEngineProfile(profileId: EngineProfileId): void {
    const nextIsEV = isEVProfile(profileId);
    this.currentProfileId = profileId;

    if (nextIsEV) {
      // Switching to or between EV Soundscapes
      if (this.synth) this.synth.stop();
      if (this.sampleLayer) this.sampleLayer.stop();

      const evConfig = EV_SOUND_PROFILES[profileId as keyof typeof EV_SOUND_PROFILES];
      if (evConfig && this.evSynth) {
        this.evSynth.setProfile(evConfig);
        if (this.isRunning) {
          this.evSynth.start();
        }
      }
    } else {
      // Switching to or between Combustion Engines
      if (this.evSynth) this.evSynth.stop();

      const conf = ENGINE_PROFILES[profileId as CombustionProfileId];
      if (conf && this.synth) {
        this.synth.setProfile(conf);
        if (this.isRunning) {
          this.synth.start();
        }
      }
    }
  }

  /**
   * Switch sound character mode (smooth, sport, aggressive)
   */
  public setCharacterMode(character: CharacterMode): void {
    this.currentCharacter = character;
    if (this.synth && !isEVProfile(this.currentProfileId)) {
      this.synth.setCharacter(character);
      this.synth.updateParameters(this.currentRpm, this.currentEffort, this.currentEngineBraking);
    }
  }

  /**
   * Master volume control (0.0 to 1.0)
   */
  public setVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.ctx && !this.isMuted) {
      this.masterGain.gain.setTargetAtTime(this.masterVolume, this.ctx.currentTime, 0.05);
    }
  }

  public getVolume(): number {
    return this.masterVolume;
  }

  /**
   * Mute / unmute audio
   */
  public setMuted(muted: boolean): void {
    this.isMuted = muted;
    if (this.masterGain && this.ctx) {
      const target = muted ? 0.0 : this.masterVolume;
      this.masterGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.04);
    }
  }

  public getIsMuted(): boolean {
    return this.isMuted;
  }

  public getDebugInfo(): AudioDebugInfo {
    if (this.synth) {
      return this.synth.getDebugInfo();
    }
    return {
      baseFrequencyHz: 0,
      fundamentalGain: 0,
      harmonicsGain: 0,
      lowFreqBodyGain: 0,
      intakeGain: 0,
      exhaustGain: 0,
      mechanicalNoiseGain: 0,
      highFreqRaspGain: 0,
      engineBrakingWhineGain: 0,
      filterCutoffHz: 0,
      filterQ: 0,
      saturationDrive: 1.0,
      masterSynthGain: 0,
      currentRpmBand: 'idle',
      audioContextState: this.ctx ? this.ctx.state : 'uninitialized',
      sampleRate: this.ctx ? this.ctx.sampleRate : 48000,
    };
  }

  public getEVDebugInfo(): EVSoundDebugInfo | null {
    if (this.evSynth && isEVProfile(this.currentProfileId)) {
      return this.evSynth.getDebugInfo();
    }
    return null;
  }

  public getEVMusicalState(): EVMusicalState | null {
    if (this.evSynth && isEVProfile(this.currentProfileId)) {
      return this.evSynth.getMusicalState();
    }
    return null;
  }

  public destroy(): void {
    this.stop();
    if (this.evSynth) {
      this.evSynth.destroy();
      this.evSynth = null;
    }
    if (this.ctx && this.ctx.state !== 'closed') {
      try {
        this.ctx.close();
      } catch {}
      this.ctx = null;
    }
  }
}
